/**
 * SSOT for passenger-facing Base Fare / Taxes / Service Charge across
 * Booking, Review, and Ancillary. Mirrors the former inline math in
 * BookingPage.jsx + ReviewBooking.jsx, with round-trip / multi-city
 * aggregation across legs (each leg may carry its own fareBreakdown).
 */

/**
 * @param {unknown[]} passengers
 * @returns {{ adults: number, children: number, infants: number }}
 */
export function derivePaxCountsFromPassengers(passengers) {
  if (!Array.isArray(passengers) || passengers.length === 0) {
    return { adults: 0, children: 0, infants: 0 };
  }
  let adults = 0;
  let children = 0;
  let infants = 0;
  for (const p of passengers) {
    const t = String(p?.pt || p?.type || p?.passengerType || '').toUpperCase();
    if (t === 'ADULT' || t === 'ADT') adults += 1;
    else if (t === 'CHILD' || t === 'CHD') children += 1;
    else if (t === 'INFANT' || t === 'INF') infants += 1;
  }
  return { adults, children, infants };
}

export function buildPaxSummaryLine(numAdults, numChildren, numInfants) {
  const parts = [];
  if (numAdults > 0) parts.push(`${numAdults} Adult${numAdults > 1 ? 's' : ''}`);
  if (numChildren > 0) parts.push(`${numChildren} Child${numChildren > 1 ? 'ren' : ''}`);
  if (numInfants > 0) parts.push(`${numInfants} Infant${numInfants > 1 ? 's' : ''}`);
  return parts.join(' · ');
}

export function buildServiceChargeSubline(numAdults, numChildren, numInfants, muAdult, muChild, muInfant) {
  const parts = [];
  if (numAdults > 0 && muAdult > 0) {
    parts.push(`₹${Math.round(muAdult).toLocaleString()} × ${numAdults} ADT`);
  }
  if (numChildren > 0 && muChild > 0) {
    parts.push(`₹${Math.round(muChild).toLocaleString()} × ${numChildren} CHD`);
  }
  if (numInfants > 0 && muInfant > 0) {
    parts.push(`₹${Math.round(muInfant).toLocaleString()} × ${numInfants} INF`);
  }
  return parts.join(' + ');
}

/**
 * @param {Record<string, unknown>|null|undefined} flightData
 * @param {string} [bookingType]
 * @returns {Record<string, unknown>[]}
 */
export function collectFareLegFlights(flightData, bookingType) {
  if (!flightData || typeof flightData !== 'object') return [];
  const bt = String(bookingType || flightData.type || flightData.bookingType || 'oneway').toLowerCase();
  if (bt === 'roundtrip') {
    return [flightData.onward, flightData.return].filter(Boolean);
  }
  if (bt === 'multicity' && Array.isArray(flightData.legs)) {
    return flightData.legs.map((x) => x?.flight).filter(Boolean);
  }
  if (bt === 'combo' && flightData.flight) {
    return [flightData.flight];
  }
  return [flightData];
}

/**
 * @param {Record<string, unknown>} leg
 * @param {number} numAdults
 * @param {number} numChildren
 * @param {number} numInfants
 */
function aggregateOneLeg(leg, numAdults, numChildren, numInfants) {
  const fb = leg?.fareBreakdown || leg?.fb;
  if (fb && (fb.adult || fb.child || fb.infant)) {
    const a = fb.adult || {};
    const c = fb.child || {};
    const i = fb.infant || {};
    const baseFare =
      numAdults * (Number(a.bf) || 0) +
      numChildren * (Number(c.bf) || 0) +
      numInfants * (Number(i.bf) || 0);
    const taxes =
      numAdults * (Number(a.taf) || 0) +
      numChildren * (Number(c.taf) || 0) +
      numInfants * (Number(i.taf) || 0);
    const muAdult = Number(a.markup || 0);
    const muChild = Number(c.markup || 0);
    const muInfant = Number(i.markup || 0);
    const serviceCharge = Math.round(
      numAdults * muAdult + numChildren * muChild + numInfants * muInfant
    );
    return {
      baseFare,
      taxes,
      serviceCharge,
      muAdult,
      muChild,
      muInfant,
      hasFb: baseFare + taxes > 0,
    };
  }

  const fares = leg?.fares;
  if (fares && (fares.adultBase || fares.childBase)) {
    const aBase = Number(fares.adultBase || 0);
    const cBase = Number(fares.childBase || aBase);
    const iBase = Number(fares.infantBase || 0);
    const muAdult = Number(leg?._markup_amount_adult || 0);
    const muChild = Number(leg?._markup_amount_child || 0);
    const muInfant = Number(leg?._markup_amount_infant || 0);
    const aGross = Number(fares.adult || 0);
    const cGross = Number(fares.child || aGross);
    const iGross = Number(fares.infant || 0);
    const aTax = Math.max(0, aGross - aBase - muAdult);
    const cTax = Math.max(0, cGross - cBase - muChild);
    const iTax = Math.max(0, iGross - iBase - muInfant);
    const baseFare = Math.round(
      numAdults * aBase + numChildren * cBase + numInfants * iBase
    );
    const taxes = Math.round(
      numAdults * aTax + numChildren * cTax + numInfants * iTax
    );
    const serviceCharge = Math.round(
      numAdults * muAdult + numChildren * muChild + numInfants * muInfant
    );
    return {
      baseFare,
      taxes,
      serviceCharge,
      muAdult,
      muChild,
      muInfant,
      hasFb: baseFare + taxes > 0,
    };
  }

  const rawBaseFare = Number(leg?.baseFare) || 0;
  const rawTaxes = Number(leg?.taxes) || 0;
  return {
    baseFare: rawBaseFare,
    taxes: rawTaxes,
    serviceCharge: 0,
    muAdult: 0,
    muChild: 0,
    muInfant: 0,
    hasFb: rawBaseFare > 0 || rawTaxes > 0,
  };
}

/**
 * Single source of truth for fare summary rows (Tripjack fareBreakdown,
 * Riya fares + stamped markups, legacy base/tax per leg).
 *
 * @param {{
 *   flightData: Record<string, unknown>|null|undefined,
 *   bookingType?: string|null,
 *   numAdults: number,
 *   numChildren: number,
 *   numInfants: number,
 *   supplierTotalOverride?: number|null,
 * }} args
 */
export function deriveDisplayFare({
  flightData,
  bookingType,
  numAdults,
  numChildren,
  numInfants,
  supplierTotalOverride,
}) {
  const na = Math.max(0, Number(numAdults) || 0);
  const nc = Math.max(0, Number(numChildren) || 0);
  const ni = Math.max(0, Number(numInfants) || 0);

  const legs = collectFareLegFlights(flightData, bookingType);
  if (legs.length === 0) {
    return {
      baseFare: 0,
      taxes: 0,
      serviceCharge: 0,
      muAdult: 0,
      muChild: 0,
      muInfant: 0,
      hasFareBreakdown: false,
      paxLine: buildPaxSummaryLine(na, nc, ni),
      muSubLine: '',
      supplierNetForQuote: 0,
    };
  }

  let baseFare = 0;
  let taxes = 0;
  let serviceCharge = 0;
  let totalMuAdult = 0;
  let totalMuChild = 0;
  let totalMuInfant = 0;
  let hasFareBreakdown = false;

  for (const leg of legs) {
    const row = aggregateOneLeg(leg, na, nc, ni);
    baseFare += row.baseFare;
    taxes += row.taxes;
    serviceCharge += row.serviceCharge;
    totalMuAdult += na * row.muAdult;
    totalMuChild += nc * row.muChild;
    totalMuInfant += ni * row.muInfant;
    if (row.hasFb) hasFareBreakdown = true;
  }

  baseFare = Math.round(baseFare);
  taxes = Math.round(taxes);
  serviceCharge = Math.round(serviceCharge);

  const muAdult = na > 0 ? totalMuAdult / na : 0;
  const muChild = nc > 0 ? totalMuChild / nc : 0;
  const muInfant = ni > 0 ? totalMuInfant / ni : 0;

  const paxLine = buildPaxSummaryLine(na, nc, ni);
  const muSubLine = buildServiceChargeSubline(na, nc, ni, muAdult, muChild, muInfant);
  let supplierNetForQuote = baseFare + taxes;

  const overrideTotal = Number(supplierTotalOverride);
  if (
    Number.isFinite(overrideTotal)
    && overrideTotal > 0
    && supplierNetForQuote > 0
  ) {
    const targetNet = Math.max(0, overrideTotal - serviceCharge);
    if (targetNet > 0) {
      const ratio = targetNet / supplierNetForQuote;
      baseFare = Math.round(baseFare * ratio);
      taxes = Math.round(taxes * ratio);
      supplierNetForQuote = baseFare + taxes;
    }
  }

  return {
    baseFare,
    taxes,
    serviceCharge,
    muAdult,
    muChild,
    muInfant,
    hasFareBreakdown,
    paxLine,
    muSubLine,
    supplierNetForQuote,
  };
}
